import * as React from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';
import { convertDateTimeFromUTCToLocal, Field, FieldType } from 'gossamer-unified';

interface EditableProps {
  className?: string;
  clearOnSelection?: boolean;
  disabled?: boolean;
  field: Field;
  handleBlur?: (newValue: string) => void;
  handleChange?: (newValue: string) => void;
  placeholder?: string;
  validity?: boolean;
  value: string;
}

const FIELD_TYPE_MAP: Partial<Record<FieldType, string>> = {
  [FieldType.Date]: 'date',
  [FieldType.Datetime]: 'datetime-local',
  [FieldType.Decimal]: 'number',
  [FieldType.Integer]: 'number',
  [FieldType.Text]: 'text',
};

export const Editable: React.FC<EditableProps> = (props) => {
  const isDateTime = () => props.field.type === FieldType.Datetime;
  const isDate = () => props.field.type === FieldType.Date;
  const convertIn = (str: string) => {
    try {
      return isDateTime() ? convertDateTimeFromUTCToLocal(str) : isDate() ? str.substring(0, 10) : str;
    } catch (e) {} // swallow date/time invalid format and return empty string
    return '';
  };
  // Raw string values required by and from <input type=datetime-local> do not include timezone
  // and are hence interpreted as local in this code, which converts them to UTC strings
  const convertOut = (str: string) => (isDateTime() ? new Date(str).toISOString() : str);
  const isNumeric = () => props.field.type === FieldType.Decimal || props.field.type === FieldType.Integer;
  // whether the text in the input already represents the given value - the value received back from the caller
  // after a change is normalised (trimmed, parsed to a number, etc.) so can differ from the raw input text
  const isEquivalent = (display: string, value: string) => {
    if (isNumeric() || isDateTime()) {
      // datetime-local input text is local time without seconds, the value is a UTC ISO string
      const a = isDateTime() ? new Date(display).getTime() : parseFloat(display);
      const b = isDateTime() ? new Date(value).getTime() : parseFloat(value);
      return a === b || (Number.isNaN(a) && Number.isNaN(b));
    }
    return display.trim() === convertIn(value || '').trim();
  };

  // the input is controlled by local state so that partially-typed text (trailing spaces, "1.", "-") is not
  // overwritten by the normalised value, but is resynced when the value is changed externally (e.g. form reset)
  const [display, setDisplay] = React.useState<string>(() => convertIn(props.value || ''));
  React.useEffect(() => {
    if (!isEquivalent(display, props.value)) {
      setDisplay(convertIn(props.value || ''));
    }
  }, [props.value]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (props.handleBlur) {
      props.handleBlur(convertOut(event.target.value.trim()));
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplay(event.target.value);
    if (props.handleChange) {
      props.handleChange(convertOut(event.target.value.trim()));
    }
  };
  const formControlLocal = (
    <FormControl
      className={props.className}
      disabled={props.disabled}
      value={display}
      isInvalid={props.validity === false}
      isValid={props.validity === true}
      onBlur={handleBlur}
      onChange={handleChange}
      placeholder={props.placeholder || 'Enter ' + (FIELD_TYPE_MAP[props.field.type] || 'value')}
      type={FIELD_TYPE_MAP[props.field.type] || 'text'}
    />
  );
  if (!props.field.staticPrefix && !props.field.staticSuffix) {
    return formControlLocal;
  }
  return (
    <InputGroup>
      {props.field.staticPrefix && <InputGroup.Text>{props.field.staticPrefix}</InputGroup.Text>}
      {formControlLocal}
      {props.field.staticSuffix && <InputGroup.Text>{props.field.staticSuffix}</InputGroup.Text>}
    </InputGroup>
  );
};
